<template>
  <article class="ArticleContentLoop">
    <StructuredText
      ref="body"
      :class="$b('wrapper')"
      :data="content"
      :render-block="renderBlock"
      :custom-rules="customRules"
    />

    <BackToTop
      :scroll-percentage="scrollPercentage"
      visible-on-desktop
      @back-to-top="backToTop"
    />
  </article>
</template>

<script>
import { StructuredText, renderRule } from 'vue-datocms'
import {
  isHeading,
  isParagraph,
  isLink,
  isBlockquote,
  isList,
} from 'datocms-structured-text-utils'

import {
  ArticleVideo,
  ArticleCarousel,
  ArticleImageWithTiles,
  ArticleProductCard,
  LargeCopy,
  SecondaryLineLink,
  BlockQuote,
  DataCaptureBlock
} from '#components'

export default {
  name: 'ArticleContentLoop',

  components: { StructuredText },

  props: {
    /**
     * Object structure is DatoCMS Structured Text.
     * https://www.datocms.com/blog/introducing-structured-text
     */
    content: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    scrollPercentage: 0,
  }),

  computed: {
    customRules() {
      return [
        renderRule(
          isHeading,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            const tag = `h${node.level === 1 ? 2 : node.level}`
            return h(
              tag,
              { key, class: `${this.$b('heading')} t-h${node.level}` },
              children
            )
          }
        ),
        renderRule(
          isParagraph,
          ({ adapter: { renderNode: h }, children, key }) => {
            if (
              children.every(
                (child) => typeof child === 'string' && child?.match(/^\s*$/)
              )
            )
              return

            return h('p', { key, class: 't-body' }, children)
          }
        ),
        renderRule(
          isLink,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            return h(
              SecondaryLineLink,
              { key, size: 'inherit', disableUnderline: true, to: node.url },
              () => children
            )
          }
        ),
        renderRule(
          isBlockquote,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            return h(
              BlockQuote,
              { key, size: 'inherit', attribution: node.attribution },
              () => children
            )
          }
        ),
        renderRule(
          isList,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            const tag = node.style === 'bulleted' ? 'ul' : 'ol'
            const listClass = node.style === 'bulleted' ? 'ul-list' : 'ol-list'
            return h(tag, { key, class: listClass }, children)
          }
        ),
      ]
    },
  },

  mounted() {
    this.handleScroll = this.throttleFunction(this.handleScroll, 50)
    window.addEventListener('scroll', this.handleScroll)

    this.addAnchorLinkListeners()

    if (this.$route.query.scrollTo) {
      this.$scrollTo(`#${this.$route.query.scrollTo}`, {
        duration: 600,
        force: false,
        offset: -60,
      })
    }
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    renderBlock({ record }) {
      const isFirst =
        this.content?.value?.document?.children?.findIndex(
          (item) => item?.type === 'block' && item?.item === record.id
        ) === 0

      switch (record.__typename) {
        case 'ArticleImageRecord': {
          return h(ArticleImageWithTiles, {
            class: { 'is-first': isFirst, 'is-block': true },
            id: `image-with-tiles-${record.id}`,
            caption: record.caption,
            credit: record.credit,
            scaleMode: record.scaleMode,
            responsiveImage: record.image.responsiveImage,
            focalPoint: record.image.focalPoint,
            tiles: record.productCards,
          })
        }

        case 'LargeCopyRecord': {
          return h(LargeCopy, {
            class: { 'is-first': isFirst, 'is-block': true },
            copy: record.copy,
            smallCopy: record.smallText,
          })
        }

        case 'ArticleVideoRecord': {
          return h(ArticleVideo, {
            class: { 'is-first': isFirst, 'is-block': true },
            video: record.video,
            caption: record.caption,
            scrollToId: record.scrollToId,
          })
        }

        case 'ArticleCarouselRecord': {
          return h(ArticleCarousel, {
            class: { 'is-first': isFirst, 'is-block': true },
            images: record.images,
            caption: record.caption,
            credit: record.credit,
          })
        }

        case 'ArticleProductCardRecord': {
          return h(ArticleProductCard, {
            class: { 'is-first': isFirst, 'is-block': true },
            id: `article-product-card-list-${record.id}`,
            heading: record.heading,
            productCards: record.productCard,
          })
        }

        case 'ArticleAnchorTargetRecord': {
          return h('div', {
            class: { 'anchor-target': true },
            id: record.sectionId,
            'aria-hidden': true,
          })
        }

        case 'InlineDataCaptureFormRecord':
          return h(DataCaptureBlock, {
            block: record.block
          })

        default: {
          return null
        }
      }
    },
    handleScroll() {
      const body = this.$refs?.body?.$el
      if (!body || body.scrollHeight < window.innerHeight * 1.5) {
        this.scrollPercentage = 0
        return
      }

      const { top } = body.getBoundingClientRect()
      this.scrollPercentage = Math.min(
        100,
        (-top / (body.scrollHeight - window.innerHeight)) * 100
      )
    },
    backToTop() {
      window.scrollTo({ top: 100, behavior: 'smooth' })
    },
    addAnchorLinkListeners() {
      const anchorLinks = this.$el.querySelectorAll('a[href^="#"]')
      anchorLinks.forEach((link) => {
        link.addEventListener('click', this.handleAnchorLinkClick)
      })
    },

    handleAnchorLinkClick(event) {
      event.preventDefault()
      const targetId = event.currentTarget.getAttribute('href').slice(1)
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' })
      }
    },
  },
}
</script>

<style lang="postcss">
.ArticleContentLoop {
  margin-bottom: -90px;

  @screen lg {
    margin-bottom: -110px;
  }
}

.ArticleContentLoop__wrapper {
  --margin-y: 60px;

  @screen lg {
    --margin-y: 80px;
  }

  margin-bottom: var(--margin-y);

  p {
    @apply mb-xs;

    @screen lg {
      max-width: var(--w-7-9);
    }
  }

  br {
    @apply block h-xxxs;

    content: '';
  }

  .ul-list {
    @apply list-disc;
  }

  .ol-list {
    @apply list-decimal;
  }

  .ul-list,
  .ol-list {
    @apply pl-xs;

    p {
      @apply mb-xxxs;
    }
  }

  mark {
    @apply bg-transparent;
  }

  .anchor-target {
    @apply invisible pt-sm -mt-sm;
  }
}

.is-block {
  --margin-y: 60px;

  @screen lg {
    --margin-y: 80px;
  }

  /* If the element is a block and NOT the last child, give margin bottom. */
  &:not(:last-child) {
    margin-bottom: var(--margin-y);
  }

  /* If the first element in the content loop is a block, add 40px mb. */
  &:first-child {
    margin-bottom: 40px;
  }

  /* If the element is a block and NOT the first child, give margin top. */
  &:not(:first-child) {
    margin-top: var(--margin-y);
  }
}

.ArticleContentLoop__heading {
  margin-bottom: 20px;
  max-width: var(--w-7-9);

  @screen lg {
    margin-bottom: 32px;
  }

  &:not(:first-child) {
    margin-top: 40px;
  }
}
</style>
